import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Trash, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PageTitle from './PageTitle';
import { CoverLetter } from '../types';
import { Session, AuthChangeEvent } from '@supabase/supabase-js';

export default function History() {
  const { user } = useAuth();
  const [session, setSession] = useState<{ access_token: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingState, setDownloadingState] = useState<{ id: string; format: 'pdf' | 'docx' } | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLetters, setTotalLetters] = useState(0);
  const LETTERS_PER_PAGE = 10;

  useEffect(() => {
    // Get the current session when component mounts
    supabase.auth.getSession().then(({ data: { session } }: { data: { session: Session | null } }) => {
      setSession(session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<CoverLetter | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [preventAutoClose, setPreventAutoClose] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchCoverLetters();
    }
  }, [user, currentPage]);

  const fetchCoverLetters = async () => {
    try {
      setLoading(true);
      
      // First, get the total count
      const countQuery = await supabase
        .from('cover_letters')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user?.id);
        
      if (countQuery.error) throw countQuery.error;
      setTotalLetters(countQuery.count || 0);
      
      // Then fetch the current page of data
      const from = (currentPage - 1) * LETTERS_PER_PAGE;
      const to = from + LETTERS_PER_PAGE - 1;
      
      const { data, error } = await supabase
        .from('cover_letters')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      setCoverLetters(data || []);
    } catch (error) {
      console.error('Error fetching cover letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLetter = (letter: CoverLetter) => {
    
    // First set the selected letter
    setSelectedLetter(letter);
    
    // Set preventAutoClose flag to true to block immediate onOpenChange false events
    setPreventAutoClose(true);
    
    // Then open the dialog with a slight delay to ensure React has processed the state change
    setTimeout(() => {
      setIsDialogOpen(true);
    }, 50);
    
    // After a longer delay, we can allow closing again
    // This is longer to ensure the dialog is fully rendered
    setTimeout(() => {
      setPreventAutoClose(false);
    }, 2000); // 2000ms (2 seconds) should be enough time for the dialog to fully render
  };
  
  // Track selectedLetter state changes
  useEffect(() => {
    // State updated
  }, [selectedLetter]);

  const handleDownload = async (letterId: string, fileId: string, format: 'pdf' | 'docx', jobInfo: { company: string, title: string }) => {
    setDownloadingState({ id: letterId, format });
    try {
      
      // Create filename from job info
      const sanitizedCompany = jobInfo.company.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const sanitizedTitle = jobInfo.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const defaultFilename = `cover_letter_${sanitizedCompany}_${sanitizedTitle}.${format}`;
      
      // Use absolute API URL from environment variable
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const url = `${apiBaseUrl}/cover-letters/files/${fileId}/stream?format=${format}`;

      
      // Set up fetch with appropriate headers
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Accept': 'application/octet-stream' // Explicitly request binary data
        }
      });
      

      
      if (!response.ok) {
        // For error responses, try to get error details
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} ${response.statusText}\nResponse: ${errorText.substring(0, 100)}...`);
      }
      
      // Get the filename from Content-Disposition header
      let filename = defaultFilename;
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];

        }
      }
      
      // Get the file as a blob
      const blob = await response.blob();

      
      // Skip download if it's clearly an HTML response (likely an error page)
      if (blob.type.includes('text/html')) {
        // Read the HTML content to debug what's going wrong
        const text = await blob.text();
        throw new Error(`Received HTML instead of file data (${blob.size} bytes)`);
      }
      
      // Create a download link and trigger it
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      

    } catch (error) {
      console.error('Download error:', error);
      // Handle the error safely with proper type checking
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Download failed: ${errorMessage}`);
    } finally {
      setDownloadingState(null);
    }
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const handleDelete = async (letterId: string) => {
    setDeletingId(letterId);
    try {
      // Update the UI first for a smoother experience
      setCoverLetters(prev => prev.filter(letter => letter.id !== letterId));
      
      
      // Use absolute API URL from environment variable
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const url = `${apiBaseUrl}/cover-letters/${letterId}`;

      
      // Set up fetch with appropriate headers
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        }
        // No mode: 'same-origin' as we're using the absolute URL
      });
      


      if (!response.ok) {
        // For error responses, try to get error details
        const errorText = await response.text();
        throw new Error(`Delete failed: ${response.status} ${response.statusText}\nResponse: ${errorText.substring(0, 100)}...`);
      }
      

      // After successful delete we already removed it from the UI
      await fetchCoverLetters();
    } catch (error) {
      console.error('Error deleting cover letter:', error);
      // Handle the error safely with proper type checking
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Delete failed: ${errorMessage}`);
      // Restore the letter if deletion failed
      await fetchCoverLetters();
    } finally {
      setDeletingId(null);
    }
  };

  const handleClose = () => {

    // Only proceed with closing if we're not in the preventAutoClose period
    if (!preventAutoClose) {
      // First close the dialog, then clear the letter to avoid flickering
      setIsDialogOpen(false);
      // Use a slight delay to ensure the dialog closing animation completes
      // before clearing the letter content
      setTimeout(() => {
        setSelectedLetter(null);
      }, 100);
    }
  };
  
  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage * LETTERS_PER_PAGE < totalLetters) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  const totalPages = Math.ceil(totalLetters / LETTERS_PER_PAGE);

  if (loading && currentPage === 1) {
    return (
      <div className="container py-8">
        <div className="space-y-3">
          <Skeleton className="h-8 w-[250px]" />
          <Skeleton className="h-[160px] w-full" />
          <Skeleton className="h-[160px] w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-4xl mx-auto">
      <PageTitle title="Dashboard" />
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        
        {totalLetters === 0 ? (
          <div className="space-y-4">
            <div className="text-muted-foreground">
              No cover letters generated yet.
            </div>
            <div className="bg-muted p-4 rounded-lg border">
              <p className="font-medium mb-2">Install the Chrome Extension to get started:</p>
              <a 
                href="https://chromewebstore.google.com/detail/bjglcmkgpkocdbidifgcjcnccofbbegn?utm_source=item-share-cb" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Quick Cover Letter for Handshake
              </a>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coverLetters.map((letter) => (
                <Card key={letter.id} className="overflow-hidden transition-all duration-300 hover:shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:-translate-y-1">
                  <CardHeader className="pb-2 px-5 pt-4">
                    <CardTitle className="truncate text-lg" title={letter.job_title}>{letter.job_title}</CardTitle>
                    <CardDescription className="truncate" title={letter.company}>{letter.company}</CardDescription>
                    <p className="text-sm text-muted-foreground italic mt-1">
                      Created on {new Date(letter.created_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}, {new Date(letter.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0 px-5 pb-2">
                  </CardContent>
                  <CardFooter className="flex justify-between px-5 pb-4">
                    <Button
                      onClick={() => handleViewLetter(letter)}
                    >
                      View Cover Letter
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(letter.id, letter.pdf_file_id, 'pdf', { company: letter.company, title: letter.job_title })}
                        disabled={downloadingState?.id === letter.id && downloadingState?.format === 'pdf'}
                      >
                        {downloadingState?.id === letter.id && downloadingState?.format === 'pdf' ? <Download size={16} className="animate-pulse" /> : 'PDF'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(letter.id, letter.docx_file_id, 'docx', { company: letter.company, title: letter.job_title })}
                        disabled={downloadingState?.id === letter.id && downloadingState?.format === 'docx'}
                      >
                        {downloadingState?.id === letter.id && downloadingState?.format === 'docx' ? <Download size={16} className="animate-pulse" /> : 'Word'}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(letter.id)}
                        disabled={deletingId === letter.id}
                      >
                        {deletingId === letter.id ? 'Deleting...' : <Trash size={16} />}
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 gap-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToPrevPage} 
                  disabled={currentPage === 1 || loading}
                >
                  <ChevronLeft size={16} />
                  <span className="ml-2">Previous</span>
                </Button>
                
                <div className="text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToNextPage} 
                  disabled={currentPage === totalPages || loading}
                >
                  <span className="mr-2">Next</span>
                  <ChevronRight size={16} />
                </Button>
              </div>
            )}
          </>
        )}

        <Dialog 
          open={isDialogOpen} 
          onOpenChange={(open) => {
            // Only allow closing if open is false AND we're not in preventAutoClose period
            if (!open) {
              if (preventAutoClose) {

              } else {
                handleClose();
              }
            } else if (open && !selectedLetter) {
              // If we're trying to open the dialog but there's no letter, 
              // don't proceed - this helps prevent flicker issues

              return false;
            }
          }}
        >
          <DialogContent 
            className="sm:max-w-3xl w-[92vw] rounded-lg shadow-lg max-h-[85vh]"
          >
            <DialogHeader className="pb-2">
              <DialogTitle className="text-xl font-semibold">
                {selectedLetter && `${selectedLetter.job_title} at ${selectedLetter.company}`}
              </DialogTitle>
              <DialogDescription className="text-sm opacity-80">
                Cover Letter Preview
              </DialogDescription>
            </DialogHeader>
            {selectedLetter && (
              <div 
                className="whitespace-pre-wrap my-4 p-5 border border-border rounded hide-scrollbar"
                style={{
                  maxHeight: "calc(70vh - 200px)",
                  overflowY: "auto",
                  scrollbarWidth: "none", /* Hide scrollbar for Firefox */
                  msOverflowStyle: "none" /* Hide scrollbar for IE/Edge */
                }}
              >
                {selectedLetter.cover_letter || 'No content available'}
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row items-center justify-end gap-3 pt-2">
              <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:mr-auto justify-center sm:justify-start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedLetter && handleDownload(selectedLetter.id, selectedLetter.pdf_file_id, 'pdf', { company: selectedLetter.company, title: selectedLetter.job_title })}
                  disabled={downloadingState?.id === selectedLetter?.id && downloadingState?.format === 'pdf'}
                >
                  {downloadingState?.id === selectedLetter?.id && downloadingState?.format === 'pdf' ? <Download size={16} className="animate-pulse" /> : 'Download PDF'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedLetter && handleDownload(selectedLetter.id, selectedLetter.docx_file_id, 'docx', { company: selectedLetter.company, title: selectedLetter.job_title })}
                  disabled={downloadingState?.id === selectedLetter?.id && downloadingState?.format === 'docx'}
                >
                  {downloadingState?.id === selectedLetter?.id && downloadingState?.format === 'docx' ? <Download size={16} className="animate-pulse" /> : 'Download Word'}
                </Button>
              </div>
              <Button variant="default" size="sm" onClick={handleClose}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
